import { Link } from "react-router-dom";
import { isAppShell } from "../../lib/platform";
import type { ReactNode } from "react";

/**
 * Compact viewport-fit auth shell. Layout:
 *   - Mobile: single column, header strip + form + slim footer.
 *   - Desktop: two-column split — quiet brand panel (left, ~38%) and the form
 *     (right). Designed to fit in 100svh on a typical laptop without scroll.
 *
 * No more verse triplet inside the shell — that anchored Phase 1's design but
 * pushed the form below the fold. The brand panel holds a single short line
 * of editorial flavour (the user's verse), and the rest is air.
 */

interface VedicVerse {
  deva: string;
  iast: string;
  english: string;
  source: string;
}

export function EditorialAuthShell({
  eyebrow,
  headline,
  lede,
  banner,
  children,
  verse,
}: {
  eyebrow: string;
  headline: ReactNode;
  /** Optional small lede below the headline. Keep it under ~110 characters. */
  lede?: ReactNode;
  /** Optional banner above the form (e.g. "Password updated."). */
  banner?: ReactNode;
  /** Form contents. */
  children: ReactNode;
  /** A short verse for the left brand panel (desktop only). */
  verse: VedicVerse;
}) {
  return (
    <div className="auth-shell">
      {/* Mobile-only top strip */}
      <header className="auth-mobile-bar lg:hidden">
        <Link to="/" className="auth-mobile-bar-brand" aria-label="Veda Union, home">
          <img src="/logo-mark.svg" alt="" className="h-7 w-7" />
          <span className="font-display text-[1.2rem] tracking-tight leading-none">
            Veda <span className="font-display-italic">Union</span>
          </span>
        </Link>
        {/* Nothing to go back TO in a standalone app — "/" is the dashboard there. */}
        {!isAppShell && (
          <Link
            to="/"
            className="font-grotesk text-[0.7rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] hover:text-[var(--color-violet)]"
          >
            ← Back
          </Link>
        )}
      </header>

      {/* Brand panel (desktop only) */}
      <aside className="auth-brand">
        <Link to="/" className="auth-brand-mark" aria-label="Veda Union, home">
          <img src="/logo-mark.svg" alt="" className="h-9 w-9" />
          <span className="font-display text-[1.5rem] tracking-tight leading-none">
            Veda <span className="font-display-italic">Union</span>
          </span>
        </Link>

        <figure className="auth-brand-verse">
          <p className="font-deva text-[1.35rem] leading-[1.4] text-[var(--color-ink)]">
            {verse.deva}
          </p>
          <p className="font-display-italic text-[0.95rem] mt-1 text-[var(--color-ink-mute)]">
            {verse.iast}
          </p>
          <hr className="rule-violet w-8 my-4" />
          <p className="font-display text-[1rem] leading-[1.45] text-[var(--color-ink)]">
            "{verse.english}"
          </p>
          <figcaption className="mt-3 font-grotesk text-[0.62rem] tracking-[0.22em] uppercase text-[var(--color-ink-mute)]">
            — {verse.source}
          </figcaption>
        </figure>

        <p className="auth-brand-foot">
          &copy; {new Date().getFullYear()} Veda Union ·{" "}
          <span className="font-display-italic normal-case tracking-normal">
            Founded by Vojko Kercan &amp; Branimir Gonan
          </span>
        </p>
      </aside>

      {/* Form panel */}
      <main className="auth-panel">
        <div className="auth-panel-inner">
          <p className="eyebrow mb-4">{eyebrow}</p>
          <h1 className="auth-headline">{headline}</h1>
          {lede && <p className="auth-lede">{lede}</p>}

          {banner && <div className="auth-banner mt-5">{banner}</div>}

          <div className="auth-form-stack mt-7">{children}</div>
        </div>
      </main>
    </div>
  );
}
